<template>
  <div id="app" :class="['app ' + $route.name, { isAuth: isAuth }]">
    <!-- Email confirmation modal at the root level outside all transitions -->
    <EmailConfirmationModal
      :visible="emailConfirmationModalVisible"
      :order-uuid="pendingOrderUuid"
      :expires-at="pendingOrderExpiresAt"
      :show-close-button="false"
      :close-on-overlay-click="false"
      @close="closeEmailConfirmationModal"
      @confirmed="handleConfirmationComplete"
    />
    
    <SessionTimeoutModal
      :visible="sessionTimeoutModalVisible"
      @close="handleSessionTimeoutModalClose"
    />
    
    <SessionTimeoutManager
      v-if="isAuth"
      @session-timeout="handleSessionTimeout"
    />
    
    <transition name="fade">
      <div id="page" class="page" v-if="pageLoaded">
        <Header />
        <router-view
          @setPaymentMethod="setPaymentMethod"
          :feePercentage="feePercentage"
          :currencies="currencies"
          :crypto="crypto"
          :rates="rates"
          @setCheckoutData="setCheckoutData"
          @setWallet="setWallet"
          ref="orderDetails"
        />
        <Footer :footerImages="footerImages" />
        <transition name="fade">
          <SignInModal
            v-if="signInModalIsVisible"
            @closeSignInModal="closeSignInModal"
            @login="login"
          />
        </transition>
        <transition name="fade">
          <RecoverModal
            v-if="recoverModalIsVisible"
            @closeRecover="closeRecover"
            @forgotPass="forgotPass"
          />
        </transition>
        <transition name="fade">
          <RegisterModal
            v-if="registerModalIsVisible"
            @closeRegister="closeRegister"
            @registration="registration"
          />
        </transition>
        <transition name="fade">
          <PersonalInfoModal
            v-if="personalInfoModalIsVisible"
            @closePersonalInfoModal="closePersonalInfoModal"
            @orderSubmit="orderSubmit"
            @setPersonalInfo="setPersonalInfo"
          />
        </transition>
        
        <transition name="fade">
          <cookie-modal></cookie-modal>
        </transition>
      </div>
    </transition>
    <Chatbot />
  </div>
</template>

<script>
import router from "@/router";
import CookieModal from "./components/CookieModal.vue";
import Header from "./components/Header.vue";
import Footer from "./components/Footer.vue";
import SignInModal from "./components/SignInModal.vue";
import RecoverModal from "./components/RecoverModal.vue";
import RegisterModal from "./components/RegisterModal.vue";
import PersonalInfoModal from "./components/PersonalInfoModal.vue";
import EmailConfirmationModal from "./components/EmailConfirmationModal.vue";
import Chatbot from "@/components/BotPressChat.vue";
import SessionTimeoutManager from "./components/SessionTimeoutManager.vue";
import SessionTimeoutModal from "./components/SessionTimeoutModal.vue";
import { mapActions } from "vuex";
import { debounce } from "lodash";

export default {
  name: "app",
  components: {
    Header,
    Footer,
    SignInModal,
    RecoverModal,
    RegisterModal,
    PersonalInfoModal,
    EmailConfirmationModal,
    CookieModal,
    Chatbot,
    SessionTimeoutManager,
    SessionTimeoutModal,
  },
  data: function () {
    return {
      pageLoaded: false,
      headerNav: [
        {
          title: "About Us",
          path: "/about-us",
        },
        {
          title: "FAQ's",
          path: "/faq",
        },
        {
          title: "Contact",
          path: "/contact",
        },
        // {
        //   title: 'Exchange',
        //   path: '/buy-crypto'
        // }
      ],
      footerNav: [
        {
          title: "FAQ's",
          path: "/faq",
        },
      ],
      signInModalIsVisible: false,
      registerModalIsVisible: false,
      recoverModalIsVisible: false,
      error: "",
      loginAttempts: 0,
      maxAttempts: 5,
      accountLocked: false,
      isAuth: false,
      emailVerified: false,
      successMessage: "",
      currencies: {},
      crypto: {},
      rates: [],
      wallets: [],
      orderHistory: [],
      giveAmount: 0,
      takeAmount: 0,
      takeCurrency: {},
      giveCurrency: "",
      paymentMethod: "",
      fiatToCrypto: true,
      personalInfoModalIsVisible: false,
      wallet: "",
      phone: "",
      countryId: "",
      city: "",
      address: "",
      postCode: "",
      birthDate: "",
      name: "",
      surname: "",
      email: "",
      faq: [],
      textPageList: [],
      socialLinks: [],
      hostname: "",
      textPageTitle: "",
      textPageHeader: "",
      textPageContent: "",
      checkoutSuccess: false,
      companyEmail: "",
      companyPhone: "",
      billingDescriptor: "",
      // minOrderPriceFiatToCrypto: null,
      minOrderPriceCryptoToFiat: null,
      legalInfo: "",
      paymentMethods: [],
      appDomain: "",
      topOrderLimit: null,
      footerImages: [],
      emailConfirmationModalVisible: false,
      pendingOrderUuid: '',
      pendingOrderExpiresAt: 0,
      sessionTimeoutModalVisible: false,
      tokenRefreshInterval: null,
      isRefreshingToken: false,
      feePercentage: null
    };
  },
  watch: {
    $route() {
      window.scrollTo(0, 0);
    },
  },
  methods: {
    ...mapActions("app", ["fetchLanguages"]),

    handleSessionTimeout() {
      // Update UI state
      this.isAuth = false;
      localStorage.setItem("isAuthenticated", false.toString());
      localStorage.setItem("emailVerified", false.toString());
      localStorage.removeItem("authToken");
      
      // Show the timeout modal
      this.sessionTimeoutModalVisible = true;
      
      // Add modal-open class to prevent scrolling
      document.body.classList.add('modal-open');
    },

    checkAuthStatus() {
      return this.$http
        .get("is-auth")
        .then((res) => {
          this.isAuth = res.data.authenticated;
          this.emailVerified = res.data.emailVerified;
          localStorage.setItem("isAuthenticated", this.isAuth.toString());
          localStorage.setItem("emailVerified", this.emailVerified.toString());

          if (!this.isAuth && this.$route.name === "OrderRequisites") {
            this.$router.push({
              name: "Home",
              query: { redirect: this.$route.path },
            });
          } else if (this.isAuth && !this.emailVerified) {
            if (router.currentRoute.name != "EmailVerificationNotice") {
              this.$router.push({ name: "EmailVerificationNotice" });
            }
           
          }
        })
        .catch(() => {
          this.isAuth = false;
          localStorage.setItem("isAuthenticated", "false"); 
          localStorage.setItem("emailVerified", "false");
          localStorage.removeItem("authToken");
        });
    },
    isEmailVerified() {
      return JSON.parse(localStorage.getItem("emailVerified") || "false");
    },
    setPaymentMethod(type) {
      this.paymentMethod = type;
    },
    getDomainName(hostName) {
      return hostName.substring(
        hostName.lastIndexOf(".", hostName.lastIndexOf(".") - 1) + 1
      );
    },
    clearError() {
      this.error = "";
    },
    openSignInModal() {
      this.registerModalIsVisible = false;
      this.recoverModalIsVisible = false;
      this.signInModalIsVisible = true;
    },
    closeSignInModal() {
      this.loginAttempts = 0;
      this.maxAttempts = 5;
      this.accountLocked = false;
      this.signInModalIsVisible = false;
      this.clearError();
    },
    openRecover() {
      this.signInModalIsVisible = false;
      this.registerModalIsVisible = false;
      this.recoverModalIsVisible = true;
    },
    closeRecover() {
      this.recoverModalIsVisible = false;
      this.clearError();
    },
    openRegister() {
      this.signInModalIsVisible = false;
      this.recoverModalIsVisible = false;
      this.registerModalIsVisible = true;
    },
    closeRegister() {
      this.registerModalIsVisible = false;
      this.clearError();
    },
    login(data) {
      let self = this;
      // Reset states before making the request
      self.error = "";
      self.accountLocked = false;
      
      self.$http
        .get("sanctum/csrf-cookie",  {baseURL: process.env.VUE_APP_DOMAIN})
        .then(() => {
          self.$http
            .post("login", data)
            .then((res) => {
              if (res.data.status == "OK") {
                // Reset login attempts on successful login
                self.loginAttempts = 0;
                self.accountLocked = false;
                
                self.isAuth = true;
                localStorage.setItem("isAuthenticated", true.toString());
                localStorage.setItem("authToken", res.data.access_token);
                
                // Set activity flag when user logs in
                window.sessionStorage.setItem('wasActive', 'true');
                window.sessionStorage.setItem('lastActivity', Date.now().toString());
                
                localStorage.setItem(
                  "emailVerified",
                  res.data.user.email_verified_at !== null
                );
                self.clearError();
                self.closeSignInModal();

                if (this.$route.query["redirect"]) {
                  self.$router.push({ path: self.$route.query["redirect"] });
                } else {
                  self.$router.push({ path: "/buy-crypto" });
                }
              } else {
                // Handle response with status ERROR
                if (res.data.locked === true) {
                  // Account is locked
                  self.accountLocked = true;
                  self.error = "";
                  self.loginAttempts = 0;
                } else if (res.data.attempts) {
                  // Failed login with attempts counter
                  self.loginAttempts = res.data.attempts;
                  if (res.data.max_attempts) {
                    self.maxAttempts = res.data.max_attempts;
                  }
                  self.error = res.data.message || "Invalid credentials";
                } else {
                  // Other error
                  self.error = res.data.message || "An error occurred during login. Please try again.";
                }
                
                self.isAuth = false;
                localStorage.setItem("isAuthenticated", false.toString());
                localStorage.setItem("emailVerified", false.toString());
                localStorage.removeItem("authToken");
              }
            })
            .catch((error) => {
              // Handle HTTP error responses (like 403 for locked accounts)
              if (error.response && error.response.status === 403 && error.response.data.locked === true) {
                // Special handling for 403 locked account errors
                self.accountLocked = true;
                self.error = "";
                self.loginAttempts = 0;
              } else if (error.response && error.response.data) {
                // Other HTTP errors with data
                if (error.response.data.locked === true) {
                  // Account is locked through other error path
                  self.accountLocked = true;
                  self.error = "";
                  self.loginAttempts = 0;
                } else if (error.response.data.attempts) {
                  // Failed login with attempts counter
                  self.loginAttempts = error.response.data.attempts;
                  if (error.response.data.max_attempts) {
                    self.maxAttempts = error.response.data.max_attempts;
                  }
                  self.error = error.response.data.message || "Invalid credentials";
                } else {
                  // Other error
                  self.error = error.response.data.message || "An error occurred during login. Please try again.";
                }
              } else {
                // Generic error fallback
                self.error = "An error occurred during login. Please try again.";
              }
              
              self.isAuth = false;
              localStorage.setItem("isAuthenticated", false.toString());
              localStorage.setItem("emailVerified", false.toString());
              localStorage.removeItem("authToken");
            });
        });
    },
    registration(data) {
      let self = this;
      if (data.password !== data.passConfirm) {
        self.error = "Password confirmation failed!";
      } else {
        self.$http
          .post("register", data)
          .then((res) => {
            if (res.data.status == "OK") {
              self.clearError();
              self.isAuth = true;
              localStorage.setItem("isAuthenticated", true.toString());
              localStorage.setItem("authToken", res.data.access_token);
              localStorage.setItem("emailVerified", false);

              self.closeRegister();

              if (this.$route.query["redirect"]) {
                self.$router.push({ path: self.$route.query["redirect"] });
              } else {
                self.$router.push({ path: "/profile" });
              }
            } else {
              self.error = res.data.message;
            }
          })
          .catch((res) => {
            this.error = res.response.data.message;
          });
      }
    },
    forgotPass(data) {
      let self = this;
      this.$http
        .post("user/request-password-reset", data)
        .then((res) => {
          if (res.data.status == "OK") {
            this.successMessage = res.data.message;
            this.error = "";
            setTimeout(() => {
              self.successMessage = "";
            }, 3000);
          } else {
            this.successMessage = "";
            this.error = res.response.data.message;
          }
        })
        .catch((error) => {
          this.successMessage = "";
          this.error = error.response.data.message;
        });
    },
    logout() {
      this.$http
        .post("logout")
        .then(() => {
          this.isAuth = false;
          localStorage.removeItem("authToken");
          localStorage.setItem("isAuthenticated", false.toString());
          localStorage.setItem("emailVerified", false.toString());

          this.$router.push({ path: "/" });
          this.openSignInModal();
        })
        .catch(() => {
          // Even if the request fails, clear the local storage
          this.isAuth = false;
          localStorage.removeItem("authToken");
          localStorage.setItem("isAuthenticated", false.toString());
          localStorage.setItem("emailVerified", false.toString());
          
          this.$router.push({ path: "/" });
          this.openSignInModal();
        });
    },
    getOrderHistory() {
      this.$http
        .get("orders/history")
        .then((res) => {
          this.orderHistory = res.data.payload;
        })
        .catch(() => {});
    },
    async getCurrencies() {
      return this.$http
        .get("currencies-data")
        .then((res) => {
          this.currencies = res.data.currencies;
          this.crypto = res.data.crypto;
          this.rates = res.data.rates;
          this.wallets = res.data.wallets;
        })
        .catch(() => {});
    },
    setCheckoutData(
      giveAmount,
      takeAmount,
      takeCurrency,
      giveCurrency,
      paymentMethod,
      fiatToCrypto
    ) {
      this.giveAmount = giveAmount;
      this.takeAmount = takeAmount;
      this.takeCurrency = takeCurrency;
      this.giveCurrency = giveCurrency;
      this.paymentMethod = paymentMethod;
      this.fiatToCrypto = fiatToCrypto;
    },
    orderSubmit() {
      let data = {
        email: this.email,
        name: this.name,
        surname: this.surname,
        phone: this.phone,
        country: this.countryId,
        city: this.city,
        address: this.address,
        postCode: this.postCode,
        paymentType: this.paymentMethod,
        source_asset: this.fiatToCrypto
          ? (this.giveCurrency?.code || this.giveCurrency?.title || this.takeCurrency.currency)
          : this.takeCurrency.crypto_currency,
        dest_asset: this.fiatToCrypto
          ? this.takeCurrency.crypto_currency
          : this.takeCurrency.currency,
        amount: this.fiatToCrypto ? this.giveAmount : this.takeAmount,
        exchange_rate: this.takeCurrency.price,
        wallet: this.wallet,
      };

      if (!this.fiatToCrypto) {
        data["birth_date"] = this.birthDate;
      }

      this.$http
        .post("orders/checkout", data)
        .then((res) => {
          if (res.data.status === 'OK') {
            // Close the personal info modal first
            this.closePersonalInfoModal();
            
            // Check if email confirmation is required
            if (res.data.awaiting_confirmation) {
              // Store the confirmation data with valid timestamps
              this.pendingOrderUuid = res.data.order_uuid;
              
              // Make sure expires_at is a number and is sensible (in the future)
              const expiresAt = res.data.expires_at || 0;
              const now = Math.floor(Date.now() / 1000);
              
              // Ensure expiry time is at least 30 seconds in the future
              if (expiresAt <= now + 30) {
                this.pendingOrderExpiresAt = now + 120; // Set a default of 2 minutes
              } else {
                this.pendingOrderExpiresAt = expiresAt;
              }
              
              // Show the email confirmation modal
              this.emailConfirmationModalVisible = true;
            } else if (res.data.redirect_url) {
              window.location = res.data.redirect_url;
            } else {
              this.changeCheckoutSuccess(true);
            }
          }
        })
        .catch((res) => {
          this.error = res.response.data.message;
        });
    },
    openPersonalInfoModal() {
      this.personalInfoModalIsVisible = true;
    },
    closePersonalInfoModal() {
      this.personalInfoModalIsVisible = false;
      this.clearError();
    },
    setPersonalInfo(
      email,
      name,
      surname,
      phone,
      postCode,
      countryId,
      city,
      address,
      birthDate
    ) {
      this.email = email;
      this.name = name;
      this.surname = surname;
      this.phone = phone;
      this.countryId = countryId;
      this.city = city;
      this.address = address;
      this.postCode = postCode;
      this.birthDate = birthDate;
    },
    setWallet(wallet) {
      this.wallet = wallet;
    },
    goToPrivacy() {
      let pageId = "";
      for (let i = 0; i < this.textPageList.length; i++) {
        if (this.textPageList[i].is_privacy) {
          pageId = this.textPageList[i].id;
          this.$router.push({
            path: "/pages/" + pageId,
            params: { data: pageId },
          });
          this.getTextPageContent(pageId);
        }
      }
    },
    goToTerms() {
      let pageId = "";
      for (let i = 0; i < this.textPageList.length; i++) {
        if (this.textPageList[i].is_terms) {
          pageId = this.textPageList[i].id;
          this.$router.push({
            path: "/pages/" + pageId,
            params: { data: pageId },
          });
          this.getTextPageContent(pageId);
        }
      }
    },
    goToAllowedCountries() {
      let pageId = "";
      for (let i = 0; i < this.textPageList.length; i++) {
        if (this.textPageList[i].is_countries) {
          pageId = this.textPageList[i].id;
          this.$router.push({
            path: "/pages/" + pageId,
            params: { data: pageId },
          });
          this.getTextPageContent(pageId);
        }
      }
    },
    goToPage(item) {
      this.$router.push({
        path: "/pages/" + item.id,
        params: { data: item.id },
      });
      this.getTextPageContent(item.id);
    },
    goToCookiesPage() {
      this.$router.push({ path: "/pages/" + 7, params: { data: 7 } });
      this.getTextPageContent(7);
    },
    getTextPageContent(id) {
      this.textPageTitle = "";
      this.textPageHeader = "";
      this.textPageContent = "";
      this.$http
        .get("static-pages/" + id)
        .then((res) => {
          this.textPageTitle = res.data.payload.title;
          this.textPageContent = res.data.payload.content;
          this.textPageHeader = res.data.payload.header;
        })
        .catch((res) => {
          if (res.response.status === 401) {
            this.openSignInModal();
          }
        });
    },
    getTextPages() {
      this.$http
        .get("static-pages")
        .then((res) => {
          this.textPageList = res.data.payload;
        })
        .catch(() => {});
    },
    getFaq() {
      this.$http
        .get("faq")
        .then((res) => {
          this.faq = res.data.payload;
        })
        .catch(() => {});
    },
    toExchange(giveAmount, takeAmount, chosenCrypto) {
      this.giveAmount = giveAmount;
      this.takeAmount = takeAmount;
      this.takeCurrency = chosenCrypto;
      this.$router.push({ path: "/buy-crypto" });
    },
    getGoogleTagManagerSnippet() {
      this.$http
        .get("settings")
        .then((res) => {
          const payload = res.data.payload;

          this.paymentMethods = payload.payment_methods
            ? payload.payment_methods
            : [];
          this.footerImages = payload.footer_images
            ? payload.footer_images
            : [];
          this.legalInfo = res.data.payload.legal_info;
          this.companyEmail = res.data.payload.support_email;
          this.companyPhone = res.data.payload.support_phone;
          this.billingDescriptor = res.data.payload.billing_descriptor;
          this.minOrderPriceFiatToCrypto = payload.min_order_fiat_to_crypto ? parseFloat(payload.min_order_fiat_to_crypto) : null;
          this.minOrderPriceCryptoToFiat = payload.min_order_crypto_to_fiat
            ? parseFloat(payload.min_order_crypto_to_fiat)
            : null;
          this.topOrderLimit = payload.top_order_limit
            ? parseFloat(payload.top_order_limit)
            : null;
          this.feePercentage = payload.fee_percentage
            ? parseFloat(payload.fee_percentage)
            : null;
          const snippet = res.data.payload.google_tag;
          const container = document.createElement("div");
          container.innerHTML = snippet;

          Array.from(container.childNodes)
            ?.filter((element) => element?.tagName === "SCRIPT")
            ?.forEach((element) => {
              document.head.appendChild(element);
            });
        })
        .catch((error) => {
          console.error("Error fetching Google Tag Manager snippet:", error);
        });
    },
    getSocialLinks() {
      this.$http
        .get("social-links")
        .then(({ data }) => {
          this.socialLinks = data.payload;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    changeCheckoutSuccess(value) {
      this.checkoutSuccess = value;
    },
    // Show email confirmation modal
    showEmailConfirmationModal() {
      this.emailConfirmationModalVisible = true;
    },
    
    // Close email confirmation modal
    closeEmailConfirmationModal() {
      this.emailConfirmationModalVisible = false;
      this.pendingOrderUuid = '';
      this.pendingOrderExpiresAt = 0;
    },
    
    // Handle when confirmation is complete
    handleConfirmationComplete() {
      // Change the checkout success status to true
      this.changeCheckoutSuccess(true);
      
      // Set pendingOrderUuid in OrderDetails component if it exists
      const orderDetailsComponent = this.$router.currentRoute.matched.find(record => record.name === 'OrderDetails');
      if (orderDetailsComponent && this.$refs.orderDetails) {
        this.$refs.orderDetails.pendingOrderUuid = this.pendingOrderUuid;
      }
      
      this.closeEmailConfirmationModal();
    },
    confirmTransaction(orderUuid, token) {
      // Directly access the confirmation API
      const apiUrl = `transaction/confirm/${orderUuid}/${token}`;
      window.location.href = apiUrl;
    },
    handleSessionTimeoutModalClose() {
      // Hide the modal
      this.sessionTimeoutModalVisible = false;
      
      // Remove modal-open class to restore scrolling
      document.body.classList.remove('modal-open');
      
      // Redirect to home page if not already there
      if (this.$route.name !== "Home") {
        this.$router.push({ name: "Home" });
      }
    },
    handleGlobalSessionTimeout() {
      // Update auth state
      this.isAuth = false;
      localStorage.setItem("isAuthenticated", "false");
      localStorage.setItem("emailVerified", "false");
      localStorage.removeItem("authToken");
      
      // Show the session timeout modal
      this.sessionTimeoutModalVisible = true;
      
      // Add modal-open class to prevent scrolling
      document.body.classList.add('modal-open');
    },
    setupGlobalTokenRefresh() {
      // Clear any existing interval
      if (this.tokenRefreshInterval) {
        clearInterval(this.tokenRefreshInterval);
      }
      
      // Set up new interval - refresh token when needed
      this.tokenRefreshInterval = setInterval(() => {
        // Only refresh if:
        // 1. User is authenticated
        // 2. Not already refreshing
        // 3. Session has been active for at least 4 minutes
        if (localStorage.getItem("isAuthenticated") === "true" && !this.isRefreshingToken) {
          const lastActivity = window.sessionStorage.getItem('lastActivity');
          const now = Date.now();
          
          // If active for more than 4 minutes, do a background refresh
          if (lastActivity && (now - parseInt(lastActivity)) > 240000) {
            this.refreshToken();
          }
        }
      }, 60000);
    },
    refreshToken() {
      // Skip if already refreshing or not authenticated
      if (this.isRefreshingToken || localStorage.getItem("isAuthenticated") !== "true") {
        return;
      }
      
      // Set refreshing flag
      this.isRefreshingToken = true;
      
      // Make refresh request
      this.$http
        .post('session/refresh')
        .then(response => {
          if (response.data.status === 'OK' && response.data.access_token) {
            // Update token in storage
            localStorage.setItem("authToken", response.data.access_token);
            
            // Reset activity timestamp
            window.sessionStorage.setItem('lastActivity', Date.now().toString());
          }
        })
        .catch(error => {
          // If 401 error, session is expired
          if (error.response && error.response.status === 401) {
            // Trigger session timeout handling
            this.handleSessionTimeout();
          }
        })
        .finally(() => {
          this.isRefreshingToken = false;
        });
    },
    setupActivityTracking() {
      // Track user interactions to detect active usage
      const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
      
      // Use debounced function to prevent excessive updates
      const debouncedTrackActivity = debounce(() => {
        // Set flag in sessionStorage (persists during page refreshes)
        window.sessionStorage.setItem('wasActive', 'true');
        
        // If user is authenticated, update the last activity timestamp
        if (localStorage.getItem('isAuthenticated') === 'true') {
          window.sessionStorage.setItem('lastActivity', Date.now().toString());
        }
      }, 300);
      
      // Add event listeners for all activity events
      activityEvents.forEach(event => {
        document.addEventListener(event, debouncedTrackActivity, { passive: true });
      });
      
      // Check if this is a new session or a refresh
      if (!window.sessionStorage.getItem('pageLoaded')) {
        // First page load of the session, set pageLoaded flag
        window.sessionStorage.setItem('pageLoaded', 'true');
        
        // If this is a fresh session (not authenticated), clear activity flag
        if (localStorage.getItem('isAuthenticated') !== 'true') {
          window.sessionStorage.removeItem('wasActive');
        } else {
          // If user is authenticated, mark as active
          window.sessionStorage.setItem('wasActive', 'true');
          window.sessionStorage.setItem('lastActivity', Date.now().toString());
        }
      }
    },
  },
  created() {
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      this.isAuth = true;
    }
  },
  mounted: async function () {
    this.fetchLanguages();
    this.getGoogleTagManagerSnippet();
    this.getSocialLinks();
    this.getFaq();
    this.getTextPages();

    this.currency = process.env.VUE_APP_CURRENCY;
    this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN;
    this.appDomain = process.env.VUE_APP_DOMAIN;
    this.hostname = this.getDomainName(window.location.hostname);
    
    await this.checkAuthStatus();

    if (this.isAuth) {
      this.getOrderHistory();
    }

    await this.getCurrencies();
    
    // Setup session management
    document.addEventListener('session-timeout', this.handleGlobalSessionTimeout);
    this.setupGlobalTokenRefresh();
    this.setupActivityTracking();

    // Finally, show the page content
    this.pageLoaded = true;
  },
  beforeDestroy() {
    // Remove global session timeout event listener
    document.removeEventListener('session-timeout', this.handleGlobalSessionTimeout);
    
    // Clear token refresh interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  },
  computed: {
    links() {
      return [...this.footerNav, ...this.textPageList];
    },
  },
};
</script>

<style>
</style>